/**
 * What a save would do to the device's own GPS interval, and what it would leave running.
 *
 * Both are about a value this page does not set. A sysadmin sets how often the watch takes a
 * position, in the device configuration form, and a carer here has no way of knowing they did —
 * so the outdoor alarm can read as configured while the device underneath it cannot deliver.
 *
 * Only reported for a watch: an absent interval means the device has no such setting and there is
 * nothing to say about it.
 */

import * as React from 'react';
import { OutdoorGeofencingMode, ProfileConfiguration } from '@ixicare/resources';

const MIN_GPS_PERIOD = 60;
export const GPS_OFF = 0;
const DEFAULT_CHECKIN_FREQUENCY = 5 * 60;

export interface GpsImpact {
  storedPeriod?: number;
  problem?: { key: string; derived: number };
  stopsCover: boolean;
  asking: boolean;
  setAsking: (next: boolean) => void;
}

export const useGpsImpact = (
  configuration: ProfileConfiguration | undefined,
  outdoorOn: boolean,
  checkinFrequency?: number
): GpsImpact => {
  const storedPeriod = configuration?.periodicGpsPeriod;
  const [asking, setAsking] = React.useState(false);

  const problem = React.useMemo(() => {
    if (!outdoorOn || storedPeriod === undefined) return undefined;
    const derived = Math.max(MIN_GPS_PERIOD, checkinFrequency ?? DEFAULT_CHECKIN_FREQUENCY);
    if (storedPeriod === GPS_OFF) return { key: 'profile-configuration.geofence.outdoor.gpsOff', derived };
    if (storedPeriod > derived) return { key: 'profile-configuration.geofence.outdoor.gpsSlow', derived };
    return undefined;
  }, [outdoorOn, storedPeriod, checkinFrequency]);

  // Turning outdoor cover off does not lower the interval it raised: nothing here knows what the
  // watch was doing before the perimeter, so it cannot be put back. Only worth asking when there is
  // something to stop - a device that reports no interval, or already has the fixes off, has none.
  const hadCover = (configuration?.outdoorGeofencingMode ?? OutdoorGeofencingMode.None) !== OutdoorGeofencingMode.None;

  return {
    storedPeriod,
    problem,
    stopsCover: hadCover && !outdoorOn && storedPeriod !== undefined && storedPeriod !== GPS_OFF,
    asking,
    setAsking,
  };
};
